import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    CircularProgress,
    LinearProgress,
    Alert,
    Snackbar,
    Tooltip,
    IconButton,
    TextField
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import {
    Sync as SyncIcon,
    Visibility as VisibilityIcon,
    CloudUpload as CloudUploadIcon,
    Image as ImageIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { syncCategories, getCategories, getSyncStatus, uploadCategoryImage, updateCategory, type Category as CategoryType } from '../../../shared/services/categoryService';
import { environment } from 'environments/environment';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';

export const Category: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [allCategories, setAllCategories] = useState<CategoryType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [uploadingCategoryId, setUploadingCategoryId] = useState<string | null>(null);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [categoryForUpload, setCategoryForUpload] = useState<CategoryType | null>(null);
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [editDisplayNameValue, setEditDisplayNameValue] = useState<string>('');
    const [updatingCategoryId, setUpdatingCategoryId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);



    // Load categories when page, size or search changes
    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getCategories({
                page: 1,
                limit: 1000
            });

            const data = response.data;
            if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
                setAllCategories(data.items);
            } else if (Array.isArray(data)) {
                setAllCategories(data);
            } else {
                setAllCategories([]);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load categories');
            setAllCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredCategories = React.useMemo(() => {
        if (!searchInput.trim()) return allCategories;
        const q = searchInput.trim().toLowerCase();
        return allCategories.filter(category =>
            (category.catName?.toLowerCase() || '').includes(q) ||
            (category.displayName?.toLowerCase() || '').includes(q)
        );
    }, [allCategories, searchInput]);

    const paginatedCategories = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredCategories.slice(start, start + pageSize);
    }, [filteredCategories, currentPage, pageSize]);

    const handleSync = async () => {
        let pollInterval: any;
        try {
            setSyncing(true);
            setError('');
            setSyncDetails(null);

            // Start the sync process
            await syncCategories();

            // Function to check status and update UI
            const checkStatus = async () => {
                try {
                    const statusRes = await getSyncStatus();
                    if (statusRes.success) {
                        setSyncDetails(statusRes.data as any);

                        if (statusRes.data.isRunning) {
                            await loadCategories();
                        } else {
                            if (pollInterval) clearInterval(pollInterval);
                            setSyncing(false);
                            if ((statusRes.data as any).lastError) {
                                setError((statusRes.data as any).lastError);
                            } else {
                                setSuccess('Categories synced successfully');
                            }
                            await loadCategories();
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    if (pollInterval) clearInterval(pollInterval);
                    setSyncing(false);
                }
            };

            // Immediate check
            await checkStatus();

            // Start polling
            pollInterval = setInterval(checkStatus, 3000);

        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start sync');
            setSyncing(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handleViewDetails = (category: CategoryType) => {
        navigate(`/category/${category.catName}`);
    };

    const getCategoryId = (category: CategoryType) => category.id || category.catName;

    const handleUploadClick = (category: CategoryType) => {
        setCategoryForUpload(category);
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !categoryForUpload) {
            e.target.value = '';
            return;
        }
        const categoryId = getCategoryId(categoryForUpload);
        if (!categoryId) {
            e.target.value = '';
            setCategoryForUpload(null);
            return;
        }
        try {
            setUploadingCategoryId(categoryId);
            setError('');
            await uploadCategoryImage(categoryId, file);
            setSuccess('Image uploaded successfully');
            await loadCategories();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploadingCategoryId(null);
            setCategoryForUpload(null);
        }
        e.target.value = '';
    };

    const getImageUrl = (imageUrl?: string | string[]) => {
        if (!imageUrl) return null;
        const url = Array.isArray(imageUrl) ? imageUrl[0] : imageUrl;
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${environment.API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const handleStartEditDisplayName = (category: CategoryType) => {
        if (!category.id) return;
        setEditingCategoryId(category.id);
        setEditDisplayNameValue(category.displayName || '');
    };

    const handleCancelEditDisplayName = () => {
        setEditingCategoryId(null);
        setEditDisplayNameValue('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handleSaveDisplayName = async (categoryId: string) => {
        try {
            setUpdatingCategoryId(categoryId);
            setError('');
            await updateCategory(categoryId, { displayName: editDisplayNameValue.trim() || undefined });
            setSuccess('Display name updated successfully');
            setEditingCategoryId(null);
            setEditDisplayNameValue('');
            await loadCategories();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update display name');
        } finally {
            setUpdatingCategoryId(null);
        }
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Categories
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search categories"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />
                    {(isSuperAdmin() || hasPermission('category.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing || loading}
                            loading={syncing}
                        >
                            {syncing ? 'Syncing...' : 'Sync Categories'}
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {/* Sync Progress Banner */}
                    <SyncProgressBanner syncing={syncing} details={syncDetails} />

                    {loading && allCategories.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && allCategories.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : filteredCategories.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() ? 'No categories match your search.' : 'No categories found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Category Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" width={120}>Icon / Image</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Display Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredCategories.length === 0}
                                emptyColSpan={5}
                                emptyMessage={searchInput.trim() ? 'No categories match your search.' : 'No categories in this page.'}
                            >
                                {Array.isArray(paginatedCategories) && paginatedCategories.map((category) => {
                                    const categoryId = category.id || category.catName;
                                    const hasValues = category.categoryValues && category.categoryValues.length > 0;

                                    const isUploading = uploadingCategoryId === categoryId;
                                    const imageUrl = getImageUrl(category.imageUrl);

                                    return (
                                        <TableRow key={categoryId} hover>
                                            <TableCell>{category.catName}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    {isUploading ? (
                                                        <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f9ff', borderRadius: 1 }}>
                                                            <CircularProgress size={28} />
                                                        </Box>
                                                    ) : imageUrl ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <img
                                                                src={imageUrl}
                                                                alt={category.catName}
                                                                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }}
                                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUploadClick(category)}
                                                                sx={{
                                                                    color: '#0EA5E9',
                                                                    border: '1px solid #0EA5E9',
                                                                    '&:hover': { backgroundColor: 'rgba(14, 165, 233, 0.1)', color: '#0284C7', borderColor: '#0284C7' },
                                                                }}
                                                                title="Update image"
                                                            >
                                                                <ImageIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Tooltip title="Upload image">
                                                            <span style={{ display: 'inline-block' }}>
                                                                <AppButton
                                                                    variant="secondary"
                                                                    size="small"
                                                                    startIcon={<CloudUploadIcon />}
                                                                    onClick={() => handleUploadClick(category)}
                                                                    disabled={!isSuperAdmin() && !hasPermission('category.update')}
                                                                >
                                                                    Upload
                                                                </AppButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {editingCategoryId === category.id ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            size="small"
                                                            value={editDisplayNameValue}
                                                            onChange={(e) => setEditDisplayNameValue(e.target.value)}
                                                            placeholder="Display name"
                                                            sx={{ minWidth: 160 }}
                                                            autoFocus
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSaveDisplayName(category.id!)}
                                                            disabled={updatingCategoryId === category.id}
                                                            sx={{ color: '#059669' }}
                                                            title="Save"
                                                        >
                                                            {updatingCategoryId === category.id ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <CheckIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelEditDisplayName}
                                                            disabled={updatingCategoryId === category.id}
                                                            sx={{ color: '#6b7280' }}
                                                            title="Cancel"
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <span>{category.displayName || '-'}</span>
                                                        {category.id && (isSuperAdmin() || hasPermission('category.update')) && (
                                                            <Tooltip title="Edit display name">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleStartEditDisplayName(category)}
                                                                    sx={{ color: '#0EA5E9', p: 0.25 }}
                                                                >
                                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span style={{
                                                    color: category.Status === 'Y' ? 'green' : 'red',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {category.Status === 'Y' ? 'Active' : 'Inactive'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {category.createdAt
                                                    ? new Date(category.createdAt).toLocaleDateString()
                                                    : '-'
                                                }
                                            </TableCell>

                                        </TableRow>
                                    );
                                })}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredCategories.length}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
